import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ExternalLink, FileText, Search, AlertCircle } from "lucide-react";
import {
  superAdminBillingService,
  type BillingInvoice,
} from "@/services/superAdminBillingService";

type Props = {
  invoices: BillingInvoice[];
  loading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
};

export default function InvoicesTable({
  invoices,
  loading,
  onLoadMore,
  hasMore,
}: Props) {
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const filteredInvoices = invoices.filter((invoice) => {
    const matchesText =
      !filter ||
      invoice.id.toLowerCase().includes(filter.toLowerCase()) ||
      invoice.number?.toLowerCase().includes(filter.toLowerCase()) ||
      invoice.subscription?.toLowerCase().includes(filter.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || invoice.status === statusFilter;

    return matchesText && matchesStatus;
  });

  if (loading && invoices.length === 0) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <div className="relative flex-grow max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search invoices..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="uncollectible">Uncollectible</SelectItem>
            <SelectItem value="void">Void</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredInvoices.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="text-center py-8 text-gray-500"
                >
                  No invoices found
                </TableCell>
              </TableRow>
            ) : (
              filteredInvoices.map((invoice) => (
                <TableRow key={invoice.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium text-sm">
                        {invoice.number || invoice.id}
                      </div>
                      {invoice.subscription && (
                        <div className="text-xs text-gray-500">
                          Sub: {invoice.subscription}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {superAdminBillingService.formatDate(invoice.created)}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div className="font-medium">
                        {superAdminBillingService.formatCurrency(
                          invoice.amount_due,
                          invoice.currency,
                        )}
                      </div>
                      {invoice.amount_paid > 0 &&
                        invoice.amount_paid !== invoice.amount_due && (
                          <div className="text-xs text-gray-500">
                            Paid:{" "}
                            {superAdminBillingService.formatCurrency(
                              invoice.amount_paid,
                              invoice.currency,
                            )}
                          </div>
                        )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={superAdminBillingService.getStatusBadgeVariant(
                        invoice.status || "",
                      )}
                    >
                      {invoice.status || "unknown"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {invoice.payment_intent && (
                      <div className="text-xs">
                        <Badge
                          variant={
                            invoice.payment_intent.status === "succeeded"
                              ? "default"
                              : invoice.payment_intent.status === "processing"
                                ? "secondary"
                                : "destructive"
                          }
                          className="text-xs"
                        >
                          {invoice.payment_intent.status}
                        </Badge>
                        {invoice.payment_intent.last_payment_error && (
                          <div className="flex items-center gap-1 mt-1 text-red-600">
                            <AlertCircle className="h-3 w-3" />
                            {invoice.payment_intent.last_payment_error.code}
                          </div>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      {invoice.hosted_invoice_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            window.open(invoice.hosted_invoice_url!, "_blank")
                          }
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                      {invoice.invoice_pdf && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            window.open(invoice.invoice_pdf!, "_blank")
                          }
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {hasMore && (
        <div className="flex justify-center">
          <Button variant="outline" onClick={onLoadMore} disabled={loading}>
            {loading ? "Loading..." : "Load More"}
          </Button>
        </div>
      )}
    </div>
  );
}
